import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { concept2Service, mapConcept2ApiError } from '../services/concept2/concept2Service';
import type { Concept2ConnectionStatus, Concept2WorkoutFilters } from '../types/concept2';
import { openConcept2OAuthPopup } from '../utils/concept2OAuth';

const concept2StatusCache = new Map<
  string,
  { status: Concept2ConnectionStatus; updatedAt: number }
>();

export function concept2QueryRoot(userId: string | undefined) {
  return ['concept2', userId] as const;
}

export function useConcept2Status() {
  const { user } = useAuth();
  const userId = user?.id;
  const cached = userId ? concept2StatusCache.get(userId) : undefined;

  const query = useQuery({
    queryKey: [...concept2QueryRoot(userId), 'status'],
    queryFn: async () => {
      const status = await concept2Service.getStatus();
      if (userId) {
        concept2StatusCache.set(userId, { status, updatedAt: Date.now() });
      }
      return status;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    retryOnMount: false,
    initialData: cached?.status,
    initialDataUpdatedAt: cached?.updatedAt,
    refetchInterval: (query) =>
      query.state.data?.importing || query.state.data?.status === 'syncing' ? 5_000 : false,
  });

  if (userId && query.data) {
    const existing = concept2StatusCache.get(userId);
    if (existing?.status !== query.data) {
      concept2StatusCache.set(userId, { status: query.data, updatedAt: Date.now() });
    }
  }

  const status = query.data ?? cached?.status;

  return {
    ...query,
    data: status,
    isLoading: query.isLoading && !status,
    isPending: query.isPending && !status,
  };
}

export function useConcept2Workouts(filters: Concept2WorkoutFilters = {}) {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: [...concept2QueryRoot(userId), 'workouts', filters],
    queryFn: () => concept2Service.listWorkouts(filters),
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
}

export function useConcept2Workout(workoutId: string | null) {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: [...concept2QueryRoot(userId), 'workout', workoutId],
    queryFn: () => {
      if (!workoutId) {
        throw new Error('concept2_workout_missing');
      }
      return concept2Service.getWorkout(workoutId);
    },
    enabled: !!userId && !!workoutId,
    staleTime: 60 * 1000,
  });
}

function useInvalidateConcept2() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return () => {
    if (!user?.id) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: concept2QueryRoot(user.id) });
  };
}

export function useConcept2Connect() {
  return useMutation({
    mutationFn: () => concept2Service.connect(),
    onSuccess: (data) => {
      if (!data.authorizationUrl) {
        return;
      }

      const popup = openConcept2OAuthPopup(data.authorizationUrl);
      if (!popup) {
        window.location.assign(data.authorizationUrl);
      }
    },
  });
}

export function useConcept2Sync() {
  const invalidate = useInvalidateConcept2();

  return useMutation({
    mutationFn: () => concept2Service.sync(),
    onSuccess: () => invalidate(),
  });
}

export function useConcept2Disconnect() {
  const invalidate = useInvalidateConcept2();

  return useMutation({
    mutationFn: () => concept2Service.disconnect(),
    onSuccess: () => invalidate(),
  });
}

export function useConcept2RemoveData() {
  const invalidate = useInvalidateConcept2();

  return useMutation({
    mutationFn: () => concept2Service.removeData(),
    onSuccess: () => invalidate(),
  });
}

export { mapConcept2ApiError };
